<template>
  <div :class="flat ? '' : 'rounded-lg border bg-card shadow-sm'">
    <div class="flex items-center justify-between" :class="flat ? 'mb-2' : 'border-b px-4 py-2.5'">
      <h3 class="text-sm font-semibold">Inventaire <span class="text-muted-foreground">({{ items.length }})</span></h3>
      <UiButton size="sm" variant="brand" @click="openCreate()">
        <PlusIcon class="h-4 w-4" /> Ajouter
      </UiButton>
    </div>

    <!-- Suggestions depuis la description Mindee -->
    <div v-if="suggestions.length" :class="flat ? 'mb-2 rounded-md border bg-muted/30 px-4 py-2' : 'border-b bg-muted/30 px-4 py-2'">
      <p class="mb-1 text-xs font-medium text-muted-foreground">Depuis la facture (Mindee)</p>
      <div class="space-y-1">
        <div v-for="(line, index) in suggestions" :key="index"
             class="flex items-center gap-2 rounded-md border bg-background px-2 py-1.5 text-sm">
          <span class="min-w-0 flex-1 truncate">{{ line.description }}</span>
          <span v-if="line.quantity" class="shrink-0 text-xs text-muted-foreground">x{{ line.quantity }}</span>
          <span v-if="line.unit_price" class="shrink-0 font-mono text-xs">{{ line.unit_price }} €</span>
          <UiButton size="sm" variant="outline" @click="openCreateFromLine(line)">
            <PlusIcon class="h-3.5 w-3.5" /> Ajouter
          </UiButton>
        </div>
      </div>
    </div>

    <div v-if="items.length" :class="flat ? 'divide-y overflow-hidden rounded-md border' : 'divide-y'">
      <div v-for="it in items" :key="it.id" class="flex items-center gap-3 px-4 py-2 text-sm">
        <div class="min-w-0 flex-1">
          <p class="truncate font-medium">{{ it.name }}</p>
          <p class="truncate text-xs text-muted-foreground">
            <span v-if="it.type">{{ it.type }} · </span>
            <span v-if="it.sku">SKU {{ it.sku }} · </span>
            Qté {{ it.qty }} · Restant {{ it.stock_remaining }}
          </p>
        </div>
        <span class="shrink-0 rounded-full px-2 py-0.5 text-xs" :style="statusStyle(it.accounting_status)">
          {{ statusLabel(it.accounting_status) }}
        </span>
        <button class="shrink-0 text-muted-foreground hover:text-foreground" title="Modifier" @click="openEdit(it)">
          <PencilIcon class="h-4 w-4" />
        </button>
        <button class="shrink-0 text-muted-foreground hover:text-destructive" title="Supprimer" @click="remove(it)">
          <TrashIcon class="h-4 w-4" />
        </button>
      </div>
    </div>

    <InventoryItemDialog
      :open="dialogOpen"
      :item="editing"
      :defaults="createDefaults"
      :companies="companies"
      @close="dialogOpen = false"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/vue/outline";
import UiButton from "@/components/ui/UiButton.vue";
import InventoryItemDialog from "@/components/inventory/InventoryItemDialog.vue";
import { INVENTORY_STATUSES, inventoryStatusLabel, useInventoryService } from "@/composables/inventoryService";

const STATUS_COLORS = {
  en_stock: "#3b82f6",
  en_service: "#10b981",
  vendu: "#a855f7",
  rebut: "#ef4444",
  perdu: "#64748b",
};

export default {
  name: "InventoryPanel",
  components: { UiButton, InventoryItemDialog, PlusIcon, PencilIcon, TrashIcon },
  props: {
    invoiceId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], default: null },
    invoiceDate: { type: String, default: null },
    lineItems: { type: Array, default: () => [] },
    // Sans carte englobante (bordure/fond), pour insertion dans une section déjà encadrée
    flat: { type: Boolean, default: false },
  },
  setup() {
    return { inventory: useInventoryService() };
  },
  data() {
    return {
      items: [],
      companies: [],
      dialogOpen: false,
      editing: null,
      createDefaults: {},
      statuses: INVENTORY_STATUSES,
    };
  },
  computed: {
    suggestions() {
      return (this.lineItems || [])
        .filter((l) => l && (l.description || "").trim())
        .map((l) => ({
          description: l.description,
          quantity: l.quantity || null,
          unit_price: l.unit_price ?? l.total_amount ?? null,
        }));
    },
  },
  watch: {
    invoiceId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    statusLabel(id) {
      return inventoryStatusLabel(id);
    },
    statusStyle(id) {
      const c = STATUS_COLORS[id] || "#64748b";
      return { backgroundColor: c + "1f", color: c };
    },
    async load() {
      if (!this.invoiceId) return;
      this.items = await this.inventory.byInvoice(this.invoiceId);
    },
    async ensureCompanies() {
      if (!this.companies.length) this.companies = await this.inventory.listCompanies();
    },
    baseDefaults() {
      return {
        date: this.invoiceDate ? String(this.invoiceDate).substr(0, 10) : null,
        company_id: this.companyId || null,
        invoice_id: this.invoiceId,
      };
    },
    async openCreate() {
      await this.ensureCompanies();
      this.editing = null;
      this.createDefaults = this.baseDefaults();
      this.dialogOpen = true;
    },
    async openCreateFromLine(line) {
      await this.ensureCompanies();
      this.editing = null;
      this.createDefaults = {
        ...this.baseDefaults(),
        name: line.description,
        qty: line.quantity || 1,
        cost_price: line.unit_price ?? null,
      };
      this.dialogOpen = true;
    },
    async openEdit(item) {
      await this.ensureCompanies();
      this.editing = item;
      this.dialogOpen = true;
    },
    async remove(item) {
      await this.inventory.remove(item.id);
      this.load();
    },
    onSaved() {
      this.load();
    },
  },
};
</script>
